import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, of, shareReplay, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface ManualManifestEntry {
  manualId: string;
  module: string;
  pageTitle: string;
  route: string | null;
  permission: string | null;
  permissions: string[];
  version: string;
  visibility: string;
  audiences: string[];
  businessProcesses: string[];
  aliases: string[];
  relatedManualIds: string[];
  lastReviewed: string | null;
  contentHash: string | null;
}
export interface ManualManifest {
  version: string;
  productVersion: string;
  generatedAt: string;
  description: string;
  entries: ManualManifestEntry[];
}

export interface HelpManualResponse {
  documentationVersion: string;
  productVersion: string;
  manual: ManualManifestEntry;
  renderedHtml: string;
  assetBaseUrl: string;
}

export interface HelpVersion {
  documentationVersion: string;
  productVersion: string;
  generatedAt: string;
  current: boolean;
}

export interface HelpSearchHit {
  manual: ManualManifestEntry;
  excerpt: string;
  score: number;
}

export interface HelpSearchResponse {
  query: string;
  total: number;
  results: HelpSearchHit[];
}

interface HelpCatalogApiResponse {
  documentationVersion: string;
  productVersion: string;
  generatedAt: string;
  entries: Omit<ManualManifestEntry, 'permission'>[];
}

/** Module metadata for display grouping. */
export interface ModuleGroup {
  key: string;
  label: string;
  icon: string;
  color: string;
  entries: ManualManifestEntry[];
}

@Injectable({ providedIn: 'root' })
export class HelpCenterService {

  private static readonly CATALOG_CACHE_KEY = 'cargomate.help.catalog.v1';
  private static readonly MANUAL_CACHE_PREFIX = 'cargomate.help.manual.v1.';

  private http = inject(HttpClient);
  private apiBase = `${environment.restApiUrl.replace(/\/+$/, '')}/help/v1`;

  private manifest$ = this.http.get<HelpCatalogApiResponse>(`${this.apiBase}/catalog`).pipe(
    map(response => this.toManifest(response)),
    tap(manifest => this.writeCache(HelpCenterService.CATALOG_CACHE_KEY, manifest)),
    catchError(() => of(this.readCache<ManualManifest>(HelpCenterService.CATALOG_CACHE_KEY)
      ?? this.emptyManifest())),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  getManifest(): Observable<ManualManifest> {
    return this.manifest$;
  }

  getModuleGroups(): Observable<ModuleGroup[]> {
    return this.manifest$.pipe(
      map(manifest => this.buildModuleGroups(manifest.entries))
    );
  }

  getManual(manualId: string): Observable<HelpManualResponse> {
    const cacheKey = `${HelpCenterService.MANUAL_CACHE_PREFIX}${manualId}`;
    return this.http.get<HelpManualResponse>(
      `${this.apiBase}/manuals/${encodeURIComponent(manualId)}`
    ).pipe(
      map(response => ({
        ...response,
        manual: this.normalizeEntry(response.manual)
      })),
      tap(response => this.writeCache(cacheKey, response)),
      catchError(error => {
        const cached = this.readCache<HelpManualResponse>(cacheKey);
        return cached ? of(cached) : throwError(() => error);
      })
    );
  }

  getVersions(): Observable<HelpVersion[]> {
    return this.http.get<HelpVersion[]>(`${this.apiBase}/versions`).pipe(
      catchError(() => of([]))
    );
  }

  search(query: string, limit = 20): Observable<HelpSearchResponse> {
    const params = new HttpParams()
      .set('q', query)
      .set('limit', limit);
    return this.http.get<HelpSearchResponse>(`${this.apiBase}/search`, { params }).pipe(
      map(response => ({
        ...response,
        results: response.results.map(hit => ({
          ...hit,
          manual: this.normalizeEntry(hit.manual)
        }))
      }))
    );
  }

  getEntry(manualId: string): Observable<ManualManifestEntry | undefined> {
    return this.manifest$.pipe(
      map(manifest => manifest.entries.find(entry => entry.manualId === manualId))
    );
  }

  searchEntries(query: string, entries: ManualManifestEntry[]): ManualManifestEntry[] {
    const normalized = query.toLowerCase().trim();
    if (!normalized) {
      return entries;
    }
    return entries.filter(entry =>
      entry.pageTitle.toLowerCase().includes(normalized)
      || entry.module.toLowerCase().includes(normalized)
      || entry.manualId.toLowerCase().includes(normalized)
      || entry.aliases.some(alias => alias.toLowerCase().includes(normalized))
      || Boolean(entry.route?.toLowerCase().includes(normalized))
    );
  }

  private toManifest(response: HelpCatalogApiResponse): ManualManifest {
    return {
      version: response.documentationVersion,
      productVersion: response.productVersion,
      generatedAt: response.generatedAt,
      description: 'CargoMate canonical user-manual catalog.',
      entries: response.entries.map(entry => this.normalizeEntry(entry))
    };
  }

  private normalizeEntry(
    entry: Omit<ManualManifestEntry, 'permission'> | ManualManifestEntry
  ): ManualManifestEntry {
    const permissions = entry.permissions ?? [];
    return {
      ...entry,
      route: entry.route ?? null,
      permissions,
      permission: 'permission' in entry
        ? entry.permission
        : permissions[0] ?? null,
      audiences: entry.audiences ?? [],
      businessProcesses: entry.businessProcesses ?? [],
      aliases: entry.aliases ?? [],
      relatedManualIds: entry.relatedManualIds ?? [],
      lastReviewed: entry.lastReviewed ?? null,
      contentHash: entry.contentHash ?? null
    };
  }

  private emptyManifest(): ManualManifest {
    return {
      version: '',
      productVersion: '',
      generatedAt: '',
      description: '',
      entries: []
    };
  }

  private readCache<T>(key: string): T | null {
    try {
      const value = localStorage.getItem(key);
      return value ? JSON.parse(value) as T : null;
    } catch {
      return null;
    }
  }

  private writeCache(key: string, value: unknown): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Help remains usable online when browser storage is unavailable or full.
    }
  }

  private buildModuleGroups(entries: ManualManifestEntry[]): ModuleGroup[] {
    const moduleMeta: Record<string, { label: string; icon: string; color: string }> = {
      shared:         { label: 'Documentation',    icon: 'fa-solid fa-book-open',          color: '#0f766e' },
      shell:          { label: 'Getting Started',  icon: 'fa-solid fa-right-to-bracket',   color: '#6b7280' },
      dashboard:      { label: 'Dashboard',        icon: 'fa-solid fa-gauge-high',          color: '#0ea5e9' },
      productivity:   { label: 'Productivity',     icon: 'fa-solid fa-list-check',          color: '#f59e0b' },
      ai:             { label: 'AI Assistant',     icon: 'fa-solid fa-robot',               color: '#6366f1' },
      shipment:       { label: 'Shipments',        icon: 'fa-solid fa-truck-fast',          color: '#10b981' },
      transportplanning: { label: 'Transport Planning', icon: 'fa-solid fa-route',          color: '#14b8a6' },
      clearance:      { label: 'Customs Clearance', icon: 'fa-solid fa-passport',           color: '#ef4444' },
      containertracking: { label: 'Container Tracking', icon: 'fa-solid fa-ship',           color: '#3b82f6' },
      fleet:          { label: 'Fleet',            icon: 'fa-solid fa-truck',               color: '#f97316' },
      gpstracking:    { label: 'GPS Tracking',     icon: 'fa-solid fa-location-dot',        color: '#e11d48' },
      fuelmanagement: { label: 'Fuel Management',  icon: 'fa-solid fa-gas-pump',            color: '#8b5cf6' },
      crm:            { label: 'CRM',              icon: 'fa-solid fa-address-book',        color: '#0891b2' },
      contract:       { label: 'Contracts',        icon: 'fa-solid fa-file-signature',      color: '#d946ef' },
      order:          { label: 'Orders',           icon: 'fa-solid fa-cart-shopping',       color: '#eab308' },
      expense:        { label: 'Expenses',         icon: 'fa-solid fa-receipt',             color: '#ec4899' },
      accounts:       { label: 'Finance & Accounts', icon: 'fa-solid fa-coins',             color: '#22c55e' },
      warehousing:    { label: 'Warehousing',      icon: 'fa-solid fa-warehouse',           color: '#a855f7' },
      posm:           { label: 'POSM',             icon: 'fa-solid fa-sign-hanging',        color: '#84cc16' },
      logisticsproject: { label: 'Logistics Projects', icon: 'fa-solid fa-diagram-project', color: '#06b6d4' },
      operations:     { label: 'Operations',       icon: 'fa-solid fa-person-digging',      color: '#b45309' },
      hr:             { label: 'HR',               icon: 'fa-solid fa-users',               color: '#f43f5e' },
      audit:          { label: 'Audit',            icon: 'fa-solid fa-magnifying-glass',    color: '#78716c' },
      workflow:       { label: 'Workflow',         icon: 'fa-solid fa-diagram-next',        color: '#0284c7' },
      businessevents: { label: 'Business Events',  icon: 'fa-solid fa-bolt',                color: '#d97706' },
      wasender:       { label: 'WhatsApp',         icon: 'fa-brands fa-whatsapp',           color: '#25d366' },
      developerplatform: { label: 'Developer API', icon: 'fa-solid fa-code',                color: '#475569' },
      reporting:      { label: 'Reports',          icon: 'fa-solid fa-chart-column',        color: '#7c3aed' },
      admin:          { label: 'Administration',   icon: 'fa-solid fa-gears',               color: '#334155' }
    };

    const grouped: Record<string, ModuleGroup> = {};
    for (const entry of entries) {
      const module = entry.module;
      if (!grouped[module]) {
        const meta = moduleMeta[module]
          ?? { label: module, icon: 'fa-solid fa-book', color: '#6b7280' };
        grouped[module] = {
          key: module,
          label: meta.label,
          icon: meta.icon,
          color: meta.color,
          entries: []
        };
      }
      grouped[module].entries.push(entry);
    }

    for (const group of Object.values(grouped)) {
      group.entries.sort((left, right) => left.pageTitle.localeCompare(right.pageTitle));
    }
    return Object.values(grouped).sort((left, right) => left.label.localeCompare(right.label));
  }
}
